import { Company, ManagerOfCompany } from '@interfaces';
import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import * as companyActions from './company.actions';
import * as userAuthActions from '@state/user-auth/user-auth.actions';
import { CompanyState, initialState } from './company.state';

function removeCompanyFromList(companies: Partial<Company>[] | null, companyId: string): Partial<Company>[] | null {
  return companies ? companies.filter(company => company.id !== companyId) : null;
}

function addCompanyToLoadedList(
  companies: Partial<Company>[] | null,
  company: Partial<Company>
): Partial<Company>[] | null {
  return companies ? [...companies.filter(existingCompany => existingCompany.id !== company.id), company] : null;
}

const reducer: ActionReducer<CompanyState, Action> = createReducer(
  initialState,

  on(
    companyActions.createCompany,
    (state): CompanyState => ({
      ...state,
      loading: true,
      errorMessage: null as any,
    })
  ),
  on(
    companyActions.createCompanySuccess,
    (state, { createdCompany }): CompanyState => ({
      ...state,
      loading: false,
      allActiveCompanies: null,
      allInactiveCompanies: null,
      loadedCompanyDetails: createdCompany,
      errorMessage: null as any,
    })
  ),
  on(
    companyActions.createCompanyError,
    (state, { errorMessage }): CompanyState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    companyActions.resetLoadedCompanyDetails,
    (state): CompanyState => ({
      ...state,
      loadedCompanyDetails: null,
      loading: false,
      errorMessage: null as any,
    })
  ),

  on(
    companyActions.getCompanyDetails,
    (state): CompanyState => ({
      ...state,
      loading: true,
      loadedCompanyDetails: null,
      errorMessage: null as any,
    })
  ),
  on(
    companyActions.getCompanyDetailsSuccess,
    (state, { company }): CompanyState => ({
      ...state,
      loading: false,
      loadedCompanyDetails: company,
      errorMessage: null as any,
    })
  ),
  on(
    companyActions.getCompanyDetailsError,
    (state, { errorMessage }): CompanyState => ({
      ...state,
      errorMessage,
      loading: false,
      loadedCompanyDetails: null,
    })
  ),

  on(
    companyActions.getAllActiveCompanies,
    companyActions.getAllInactiveCompanies,
    (state): CompanyState => ({
      ...state,
      loading: true,
      errorMessage: null as any,
    })
  ),
  on(
    companyActions.getAllActiveCompaniesSuccess,
    (state, { companies }): CompanyState => ({
      ...state,
      companies: companies,
      allActiveCompanies: companies,
      loading: false,
      errorMessage: null as any,
    })
  ),
  on(
    companyActions.getAllInactiveCompaniesSuccess,
    (state, { companies }): CompanyState => ({
      ...state,
      companies: companies,
      allInactiveCompanies: companies,
      loading: false,
      errorMessage: null as any,
    })
  ),
  on(
    companyActions.getAllActiveCompaniesError,
    companyActions.getAllInactiveCompaniesError,
    (state, { errorMessage }): CompanyState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    companyActions.updateCompany,
    (state): CompanyState => ({
      ...state,
      loading: true,
      errorMessage: null as any,
    })
  ),
  on(
    companyActions.updateCompanySuccess,
    (state, { updatedCompany }): CompanyState => ({
      ...state,
      loading: false,
      errorMessage: null as any,
      companies: state.companies.map(company => (company.id === updatedCompany.id ? updatedCompany : company)),
      allActiveCompanies: null,
      allInactiveCompanies: null,
      loadedCompanyDetails:
        state.loadedCompanyDetails && state.loadedCompanyDetails.id === updatedCompany.id
          ? updatedCompany
          : state.loadedCompanyDetails,
    })
  ),
  on(
    companyActions.updateCompanyError,
    (state, { errorMessage }): CompanyState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    companyActions.setIsActiveCompany,
    (state): CompanyState => ({
      ...state,
      loading: true,
      errorMessage: null as any,
    })
  ),
  on(
    companyActions.setIsActiveCompanySuccess,
    (state, { updatedCompany }): CompanyState => ({
      ...state,
      loading: false,
      errorMessage: null as any,
      companies: state.companies.map(companyObj =>
        companyObj.id === updatedCompany.id ? { ...updatedCompany } : companyObj
      ),
      allActiveCompanies:
        updatedCompany.isActive === false
          ? removeCompanyFromList(state.allActiveCompanies, updatedCompany.id)
          : addCompanyToLoadedList(state.allActiveCompanies, updatedCompany),
      allInactiveCompanies:
        updatedCompany.isActive === false
          ? addCompanyToLoadedList(state.allInactiveCompanies, updatedCompany)
          : removeCompanyFromList(state.allInactiveCompanies, updatedCompany.id),
      loadedCompanyDetails:
        state.loadedCompanyDetails && state.loadedCompanyDetails.id === updatedCompany.id
          ? updatedCompany
          : state.loadedCompanyDetails,
    })
  ),
  on(
    companyActions.setIsActiveCompanyError,
    (state, { errorMessage }): CompanyState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    companyActions.searchManagersOfCompany,
    (state): CompanyState => ({
      ...state,
      loadingManagersList: true,
      errorMessage: null as any,
    })
  ),
  on(
    companyActions.searchManagersOfCompanySuccess,
    (state, { managers }): CompanyState => ({
      ...state,
      managers: managers.data,
      managersPagination: managers.pagination,
      loadingManagersList: false,
      errorMessage: null as any,
    })
  ),
  on(
    companyActions.searchManagersOfCompanyError,
    (state, { errorMessage }): CompanyState => ({
      ...state,
      loadingManagersList: false,
      errorMessage,
    })
  ),

  on(
    companyActions.createManager,
    companyActions.updateManager,
    companyActions.getManagerDetails,
    companyActions.resendManagerActivation,
    companyActions.softRemoveManager,
    companyActions.restoreManager,
    (state): CompanyState => ({
      ...state,
      loadingManagerDetails: true,
      errorMessage: null as any,
    })
  ),
  on(
    companyActions.resendManagerActivationSuccess,
    (state): CompanyState => ({
      ...state,
      loadingManagerDetails: false,
      errorMessage: null as any,
    })
  ),
  on(
    companyActions.resendManagerActivationError,
    (state, { errorMessage }): CompanyState => ({
      ...state,
      loadingManagerDetails: false,
      errorMessage,
    })
  ),

  on(
    companyActions.createManagerSuccess,
    companyActions.getManagerDetailsSuccess,
    companyActions.updateManagerSuccess,
    companyActions.softRemoveManagerSuccess,
    companyActions.restoreManagerSuccess,
    (state, { manager }): CompanyState => ({
      ...state,
      loadingManagerDetails: false,
      loadedManagerDetails: manager,
      errorMessage: null as any,
    })
  ),
  on(
    companyActions.createManagerError,
    companyActions.getManagerDetailsError,
    companyActions.addManagedCompanyError,
    companyActions.removeManagedCompanyError,
    companyActions.updateManagerError,
    companyActions.softRemoveManagerError,
    companyActions.restoreManagerError,
    (state, { errorMessage }): CompanyState => ({
      ...state,
      loadingManagerDetails: false,
      errorMessage,
    })
  ),
  on(
    companyActions.addManagedCompany,
    companyActions.removeManagedCompany,
    (state): CompanyState => ({
      ...state,
      loadingManagerDetails: true,
    })
  ),
  on(
    companyActions.addManagedCompanySuccess,
    (state, { managedCompany }): CompanyState => ({
      ...state,
      loadingManagerDetails: false,
      loadedManagerDetails: {
        ...state.loadedManagerDetails,
        managingCompanies: [{ ...managedCompany } as ManagerOfCompany, ...state.loadedManagerDetails.managingCompanies],
      },
    })
  ),
  on(
    companyActions.removeManagedCompanySuccess,
    (state, { removedMoc }): CompanyState => ({
      ...state,
      loadingManagerDetails: false,
      loadedManagerDetails: {
        ...state.loadedManagerDetails,
        managingCompanies: state.loadedManagerDetails.managingCompanies.filter(moc => moc.id !== removedMoc.id),
      },
    })
  ),

  on(
    companyActions.getCompanyOfSelfRegistered,
    (state): CompanyState => ({
      ...state,
      loadingCompanyOfSelfRegistered: true,
    })
  ),
  on(
    companyActions.getCompanyOfSelfRegisteredSuccess,
    (state, { company }): CompanyState => ({
      ...state,
      loadingCompanyOfSelfRegistered: false,
      companyOfSelfRegistered: company,
    })
  ),
  on(
    companyActions.getCompanyOfSelfRegisteredError,
    (state, { errorMessage }): CompanyState => ({
      ...state,
      loadingCompanyOfSelfRegistered: false,
      errorMessage,
    })
  ),

  on(
    userAuthActions.logout,
    (state): CompanyState => ({
      ...state,
      companies: [],
      allActiveCompanies: null,
      allInactiveCompanies: null,
    })
  )
);

export function companyReducer(state: CompanyState | undefined, action: Action): CompanyState {
  return reducer(state, action);
}
